class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    // Disable auto-submit on input change
    // facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    // Add submit handler for filter button (desktop)
    const filterButton = facetForm.querySelector('.button--full-width');
    if (filterButton) {
      filterButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.onSubmitHandler(event);
      });
    }

    // Add submit handler for mobile main apply button
    const mobileApplyButton = facetForm.querySelector('.mobile-facets__apply');
    if (mobileApplyButton) {
      mobileApplyButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.onSubmitHandler(event);
        // Close the drawer after submitting
        setTimeout(() => {
          const drawer = this.closest('.mobile-facets__wrapper');
          if (drawer) {
            drawer.querySelector('summary').click();
          }
        }, 100);
      });
    }

    // Add submit handler for mobile individual filter apply buttons
    const mobileFilterButtons = facetForm.querySelectorAll('.mobile-facets__footer .button--primary');
    mobileFilterButtons.forEach((button) => {
      // Skip if it's the main apply button (already handled above)
      if (button.classList.contains('mobile-facets__apply')) return;

      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.onSubmitHandler(event);
        // The onclick handler will close the submenu, so we don't need to do it here
      });
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  connectedCallback() {
    // Add change handler for desktop/PC sorting select (in FacetSortForm)
    const pcSortSelect = document.querySelector('#SortBy');
    if (pcSortSelect && !pcSortSelect.dataset.listenerAdded) {
      pcSortSelect.dataset.listenerAdded = 'true';
      pcSortSelect.addEventListener('change', (event) => {
        // Find the sort form and trigger submission
        const sortForm = document.querySelector('#FacetSortForm');
        if (sortForm && sortForm.closest('facet-filters-form')) {
          const facetFiltersForm = sortForm.closest('facet-filters-form');
          facetFiltersForm.onSubmitHandler(event);
        }
      });
    }

    // Add change handler for mobile top sorting select (outside the form)
    const mobileSortSelectOutside = document.querySelector('#SortBy-mobile-top');
    if (mobileSortSelectOutside && !mobileSortSelectOutside.dataset.listenerAdded) {
      mobileSortSelectOutside.dataset.listenerAdded = 'true';
      mobileSortSelectOutside.addEventListener('change', (event) => {
        // Sync the value to the form's internal sort select
        const mobileSortSelectInside = document.querySelector('#SortBy-mobile');
        if (mobileSortSelectInside) {
          mobileSortSelectInside.value = event.target.value;
        }

        // Find the mobile form and trigger submission
        const mobileForm = document.querySelector('#FacetFiltersFormMobile');
        if (mobileForm && mobileForm.closest('facet-filters-form')) {
          const facetFiltersForm = mobileForm.closest('facet-filters-form');
          // Create a synthetic event from the inside select to maintain compatibility
          const syntheticEvent = new Event('change', { bubbles: true });
          Object.defineProperty(syntheticEvent, 'target', { value: mobileSortSelectInside || event.target, enumerable: true });
          Object.defineProperty(syntheticEvent, 'srcElement', { value: mobileSortSelectInside || event.target, enumerable: true });
          facetFiltersForm.onSubmitHandler(syntheticEvent);
        }
      });
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      const collection = productGridContainer.querySelector('.collection');
      if (collection) {
        collection.classList.add('loading');
      }
    }

    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (!productGridContainer) return;

    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const newProductGridContainer = parsedHTML.getElementById('ProductGridContainer');

    if (!newProductGridContainer) return;

    productGridContainer.innerHTML = newProductGridContainer.innerHTML;

    productGridContainer
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const productCountElement = parsedHTML.getElementById('ProductCount');

    if (!productCountElement) return;

    const count = productCountElement.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }

    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        // Save open state before updating
        const wasOpen = currentElement.classList.contains('is-open') || currentElement.hasAttribute('open');
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
        // Restore open state after updating
        if (wasOpen) {
          const updatedElement = document.getElementById(elementToRender.id);
          updatedElement.classList.add('is-open');
          updatedElement.setAttribute('open', '');
          const content = updatedElement.querySelector('.accordion__content');
          if (content) {
            content.classList.add('is-open');
          }
          const summary = updatedElement.querySelector('summary');
          if (summary) {
            summary.setAttribute('aria-expanded', 'true');
          }
        }
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    // Re-initialize accordion after updating filters
    if (typeof initFacAccordion === 'function') {
      initFacAccordion();
    }

    // Re-attach event listeners to mobile filter buttons after DOM update
    FacetFiltersForm.reattachMobileButtonListeners();

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();

    // Sync custom availability filter checkboxes
    FacetFiltersForm.syncAvailabilityCheckboxes(html);

    // Re-attach event listener to PC/desktop sort select after DOM update
    FacetFiltersForm.reattachPcSortListener();
  }

  static syncAvailabilityCheckboxes(html) {
    // Sync desktop checkbox
    const desktopCheckboxNew = html.querySelector('.show-in-stock-only-checkbox:not(.mobile-facets__checkbox)');
    const desktopCheckboxCurrent = document.querySelector('.show-in-stock-only-checkbox:not(.mobile-facets__checkbox)');
    if (desktopCheckboxNew && desktopCheckboxCurrent) {
      desktopCheckboxCurrent.checked = desktopCheckboxNew.checked;
    }

    // Sync mobile checkbox
    const mobileCheckboxNew = html.querySelector('.mobile-facets__checkbox.show-in-stock-only-checkbox');
    const mobileCheckboxCurrent = document.querySelector('.mobile-facets__checkbox.show-in-stock-only-checkbox');
    if (mobileCheckboxNew && mobileCheckboxCurrent) {
      mobileCheckboxCurrent.checked = mobileCheckboxNew.checked;
    }
  }

  static reattachPcSortListener() {
    // Re-attach event listener to PC/desktop sort select
    const pcSortSelect = document.querySelector('#SortBy');
    if (pcSortSelect) {
      // Remove the flag to allow re-attachment
      delete pcSortSelect.dataset.listenerAdded;

      // Add the change listener
      pcSortSelect.dataset.listenerAdded = 'true';
      pcSortSelect.addEventListener('change', (event) => {
        // Find the sort form and trigger submission
        const sortForm = document.querySelector('#FacetSortForm');
        if (sortForm && sortForm.closest('facet-filters-form')) {
          const facetFiltersForm = sortForm.closest('facet-filters-form');
          facetFiltersForm.onSubmitHandler(event);
        }
      });
    }
  }

  static reattachMobileButtonListeners() {
    // Re-attach event listeners to mobile individual filter apply buttons
    const mobileFilterButtons = document.querySelectorAll('#FacetFiltersFormMobile .mobile-facets__footer .button--primary');
    mobileFilterButtons.forEach((button) => {
      // Skip if it's the main apply button or already has listener
      if (button.classList.contains('mobile-facets__apply') || button.dataset.listenerAttached) return;

      button.dataset.listenerAttached = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const form = event.target.closest('facet-filters-form');
        if (form) {
          form.onSubmitHandler(event);
        }
      });
    });

    // Re-attach event listener to main mobile apply button
    const mobileApplyButton = document.querySelector('#FacetFiltersFormMobile .mobile-facets__apply');
    if (mobileApplyButton && !mobileApplyButton.dataset.listenerAttached) {
      mobileApplyButton.dataset.listenerAttached = 'true';
      mobileApplyButton.addEventListener('click', (event) => {
        event.preventDefault();
        const form = event.target.closest('facet-filters-form');
        if (form) {
          form.onSubmitHandler(event);
          // Close the drawer after submitting
          setTimeout(() => {
            const drawer = form.closest('.mobile-facets__wrapper');
            if (drawer) {
              drawer.querySelector('summary').click();
            }
          }, 100);
        }
      });
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    const url = `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`;
    // Include url property to maintain compatibility with page-transition.js
    // Also add a flag to identify this as a facet filter state
    history.pushState({ searchParams, url, isFacetFilter: true }, '', url);
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return [];

    return [
      {
        section: productGrid.dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams(formData);

    // Handle custom in-stock filter
    const showInStockOnly = form.querySelector('.show-in-stock-only-checkbox');
    if (showInStockOnly) {
      // Remove the custom checkbox from params
      params.delete('show_in_stock_only');

      // If checkbox is checked, add availability filter to show only in-stock items
      if (showInStockOnly.checked) {
        // Try common Shopify availability filter values
        // The actual value depends on your Shopify setup
        params.set('filter.v.availability', '1');
      } else {
        // If NOT checked (default), remove availability filter to show all items (including out of stock)
        params.delete('filter.v.availability');
      }
    }

    return params.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const closestForm = event.target.closest('form');
      const isMobile = closestForm ? closestForm.id === 'FacetFiltersFormMobile' : event.target.id === 'SortBy-mobile-top';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();


class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

(function initMobileFacetsVisibilityObserver() {
  const setup = () => {
    const target = document.querySelector('.product-grid-container');
    const wrappers = document.querySelectorAll('.mobile-facets__wrapper');
    if (!target || wrappers.length === 0) return;

    const setVisible = (visible) => {
      wrappers.forEach((wrapper) => {
        wrapper.classList.toggle('is-hidden', !visible);
      });
    };

    setVisible(false);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setVisible(entry.isIntersecting);
        });
      },
      { threshold: 0 }
    );

    observer.observe(target);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();
